import {
  asBooleanOrNull,
  asNumberOrNull,
  asStringOrNull,
  firstValue,
} from '@/shared/utils/type-utils';
import { COPARTICIPATION_STATEMENT_TYPE } from './coparticipation-config';
import {
  CoparticipationFilter,
  CoparticipationModel,
  CoparticipationPagination,
  CoparticipationRecord,
} from './coparticipation-types';

export const defaultFilter = (): CoparticipationFilter => ({
  carrierId: null,
  financialGroupId: null,
  contractId: null,
  competence: null,
  beneficiary: null,
  copayExempt: null,
});

export const defaultPagination = (): CoparticipationPagination => ({
  page: 1,
  itemsPerPage: 20,
  totalItems: 0,
});

export const initState: CoparticipationModel = {
  filter: defaultFilter(),
  pagination: defaultPagination(),
  loadingPage: false,
  loadingTable: false,
  loadingExport: false,
  records: [],
  showSnackbarCustomize: { show: false, type: null, message: '' },
  hasSearched: false,
};

export const buildFilterParams = (filter: CoparticipationFilter): Record<string, unknown> => {
  const params: Record<string, unknown> = {
    statementType: COPARTICIPATION_STATEMENT_TYPE,
  };

  if (filter.carrierId !== null && filter.carrierId !== undefined) {
    params.carrierId = filter.carrierId;
  }
  if (filter.financialGroupId !== null && filter.financialGroupId !== undefined) {
    params.financialGroupId = filter.financialGroupId;
  }
  if (filter.contractId !== null && filter.contractId !== undefined) {
    params.contractId = filter.contractId;
  }
  if (filter.competence) params.competence = filter.competence;
  if (filter.beneficiary) params.beneficiary = filter.beneficiary;
  if (filter.copayExempt !== null && filter.copayExempt !== undefined) {
    params.copayExempt = filter.copayExempt;
  }

  return params;
};

const namedEntityToString = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return asStringOrNull(record.name ?? record.description ?? record.legalName ?? record.id);
  }
  return null;
};

const contentFromPayload = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object' || payload === null) return [];

  const data = payload as Record<string, unknown>;
  if (Array.isArray(data.content)) return data.content;
  if (Array.isArray(data.data)) return data.data;

  const nestedData = data.data;
  if (
    typeof nestedData === 'object'
    && nestedData !== null
    && Array.isArray((nestedData as Record<string, unknown>).content)
  ) {
    return (nestedData as Record<string, unknown>).content as unknown[];
  }

  return [];
};

export const normalizePage = (payload: unknown) => {
  const pagePayload = typeof payload === 'object' && payload !== null && 'data' in payload
    ? (payload as Record<string, unknown>).data ?? payload
    : payload ?? {};
  const pageData = pagePayload as Record<string, unknown>;
  const content = contentFromPayload(pagePayload);
  const totalElements = Number(
    pageData.totalElements
      ?? pageData.total
      ?? (typeof pageData.data === 'object' && pageData.data !== null
        ? (pageData.data as Record<string, unknown>).totalElements
        : undefined)
      ?? content.length,
  );

  return {
    content,
    totalElements: Number.isNaN(totalElements) ? content.length : totalElements,
  };
};

export const normalizeRecord = (row: unknown, index = 0): CoparticipationRecord => {
  const id = String(
    firstValue(row, ['id', 'recordId']) ?? `row-${index}`,
  );

  return {
    id,
    carrier: namedEntityToString(firstValue(row, ['carrier', 'carrierName'])),
    financialGroup: namedEntityToString(firstValue(row, [
      'financialGroup',
      'financialGroupName',
    ])),
    contract: namedEntityToString(firstValue(row, [
      'contract',
      'contractNumber',
      'policyNumber',
      'contract.name',
    ])),
    subcontract: namedEntityToString(firstValue(row, [
      'subcontract',
      'subContract',
    ])),
    competence: asStringOrNull(firstValue(row, ['competence', 'competency'])),
    beneficiary: namedEntityToString(firstValue(row, [
      'beneficiary',
      'beneficiaryName',
    ])),
    ownership: asStringOrNull(firstValue(row, [
      'ownership',
      'beneficiaryType',
      'titularity',
      'holderType',
    ])),
    plan: namedEntityToString(firstValue(row, ['plan', 'planName'])),
    event: namedEntityToString(firstValue(row, ['event', 'eventName'])),
    eventDate: asStringOrNull(firstValue(row, ['eventDate', 'eventAt'])),
    provider: namedEntityToString(firstValue(row, [
      'provider',
      'providerName',
    ])),
    copayAmount: asNumberOrNull(firstValue(row, [
      'copayAmount',
      'coparticipationAmount',
      'amount',
    ])),
    copayExempt: asBooleanOrNull(firstValue(row, [
      'copayExempt',
      'coparticipationExempt',
      'isExempt',
    ])),
    reason: asStringOrNull(firstValue(row, ['reason', 'exemptReason'])),
  };
};

export const normalizeRecordsPage = (payload: unknown) => {
  const page = normalizePage(payload);
  return {
    content: page.content.map((row, index) => normalizeRecord(row, index)),
    totalElements: page.totalElements,
  };
};
